import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, ArrowUpRight, CalendarDays, Loader2, CheckCircle2, Instagram } from "lucide-react";
import { Reveal } from "./Reveal";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALENDLY = "https://calendly.com/ankitagogia/30min";
const LINKEDIN = "https://www.linkedin.com/in/ankita-gogia-4ab9011b";
const BEHANCE = "https://www.behance.net/ankitagogia";
const INSTAGRAM = "https://www.instagram.com/ankitagogiadesigns/";
const EMAIL = "ankitagogiaillustrator@gmail.com";

const SERVICES = [
  "Brand Strategy",
  "Brand Identity",
  "Website & Landing Page",
  "Social Media Design",
  "Presentation Design",
  "Creative Retainer",
  "Other",
];
const BUDGETS = ["Under $500", "$500–$1,000", "$1,000–$2,500", "$2,500+", "Let's Discuss"];
const TIMELINES = ["ASAP", "Within 2 Weeks", "Within 1 Month", "Flexible"];

const EMPTY = {
  name: "", company: "", email: "", phone: "", website: "",
  service: "", budget: "", timeline: "", message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your full name.");
    if (!form.email.trim() || !emailValid(form.email)) return toast.error("Please enter a valid email address.");
    if (!form.message.trim()) return toast.error("Please tell me a little about your project.");

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Inquiry sent successfully!");
    } catch (err) {
      toast.error("Something went wrong. Please email me directly at " + EMAIL);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-cream/25 py-3 text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none transition-colors duration-300";

  const DarkSelect = ({ value, onValueChange, placeholder, options, testid }) => (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={placeholder}
        data-testid={testid}
        className="w-full bg-transparent border-0 border-b border-cream/25 rounded-none py-3 h-auto text-cream data-[placeholder]:text-cream/40 focus:ring-0 focus:border-gold px-0 hover:border-cream/40"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-cream border-charcoal/10 text-charcoal">
        {options.map((o) => (
          <SelectItem key={o} value={o} className="cursor-pointer focus:bg-burgundy focus:text-cream">
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <section id="contact" className="py-24 md:py-32 lg:py-40 bg-charcoal text-cream" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs tracking-[0.25em] uppercase font-semibold text-gold">Contact</span>
          </div>
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-cream max-w-4xl">
            Let's build something <span className="italic text-gold">remarkable.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form / Success */}
          <div className="lg:col-span-7">
            {sent ? (
              <div
                className="flex flex-col items-start gap-5 rounded-3xl border border-gold/40 bg-cream/5 p-10 md:p-14"
                data-testid="contact-success"
                role="status"
                aria-live="polite"
              >
                <CheckCircle2 size={48} className="text-gold" />
                <h3 className="font-heading text-3xl md:text-4xl text-cream">Thank you!</h3>
                <p className="text-cream/80 text-lg leading-relaxed max-w-lg">
                  Your inquiry has been sent successfully. I'll get back to you within 1–2 business days.
                </p>
                <button
                  onClick={() => { setForm(EMPTY); setSent(false); }}
                  className="mt-2 inline-flex items-center gap-2 rounded-full border border-cream/25 text-cream hover:border-gold hover:text-gold px-6 py-3 text-sm font-medium transition-colors"
                  data-testid="contact-send-another"
                >
                  Send another inquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-7" data-testid="contact-form" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <input className={inputCls} placeholder="Full name *" aria-label="Full name" value={form.name} onChange={update("name")} data-testid="contact-name" />
                  <input className={inputCls} placeholder="Company name" aria-label="Company name" value={form.company} onChange={update("company")} data-testid="contact-company" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <input className={inputCls} type="email" placeholder="Email address *" aria-label="Email address" value={form.email} onChange={update("email")} data-testid="contact-email" />
                  <input className={inputCls} placeholder="Phone number (optional)" aria-label="Phone number" value={form.phone} onChange={update("phone")} data-testid="contact-phone" />
                </div>
                <input className={inputCls} placeholder="Website (optional)" aria-label="Website" value={form.website} onChange={update("website")} data-testid="contact-website" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <DarkSelect value={form.service} onValueChange={(v) => setField("service", v)} placeholder="Service required" options={SERVICES} testid="contact-service" />
                  <DarkSelect value={form.budget} onValueChange={(v) => setField("budget", v)} placeholder="Estimated budget" options={BUDGETS} testid="contact-budget" />
                </div>
                <DarkSelect value={form.timeline} onValueChange={(v) => setField("timeline", v)} placeholder="Preferred timeline" options={TIMELINES} testid="contact-timeline" />

                <textarea className={inputCls + " resize-none"} rows={4} placeholder="Tell me about your project *" aria-label="Tell me about your project" value={form.message} onChange={update("message")} data-testid="contact-message" />

                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-2 rounded-full bg-cream text-charcoal hover:bg-gold px-8 py-4 text-sm font-medium tracking-wide transition-colors duration-300 disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Sending..." : "Send Inquiry"}
                  {!loading && <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />}
                </button>
              </form>
            )}
          </div>

          {/* Alt contacts */}
          <div className="lg:col-span-5 lg:pl-8 lg:border-l border-cream/10">
            <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between rounded-2xl border border-cream/15 hover:border-gold p-5 transition-colors duration-300 mb-8" data-testid="contact-calendly">
              <div className="flex items-center gap-4">
                <CalendarDays size={22} className="text-gold" />
                <div>
                  <p className="font-medium text-cream">Book a 30-min call</p>
                  <p className="text-sm text-cream/50">Free discovery session</p>
                </div>
              </div>
              <ArrowUpRight size={20} className="text-cream/50 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </a>

            <div className="space-y-5">
              <a href={`mailto:${EMAIL}`} className="group flex items-center gap-4 text-cream/80 hover:text-gold transition-colors" data-testid="contact-email-link">
                <Mail size={18} /> <span>{EMAIL}</span>
              </a>
              <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 text-cream/80 hover:text-gold transition-colors" data-testid="contact-linkedin">
                <Linkedin size={18} /> <span>LinkedIn — Ankita Gogia</span>
              </a>
              <a href={BEHANCE} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 text-cream/80 hover:text-gold transition-colors" data-testid="contact-behance">
                <ArrowUpRight size={18} /> <span>Behance Portfolio</span>
              </a>
              <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 text-cream/80 hover:text-gold transition-colors" data-testid="contact-instagram">
                <Instagram size={18} /> <span>Instagram — @ankitagogiadesigns</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
